import cn from 'classnames'
import {FC} from 'react'

import {Text, TextFontWeight, TextVariant} from '@/components/common/content/Text'
import {EXPAND_TRANSITION} from '@/theme/cssTransitions'
import {theme} from '@/theme/theme'
import {MOBILE_SCREENSIZE} from '@/utils/styles/breakpoints'

type FormRadioButtonProps = {
  title: string
  subtitle?: string
  isSelected: boolean
  isCentered?: boolean
  isMobileFullWidth?: boolean
  onChange?: () => void
  isDisabled?: boolean
  size?: 'small' | 'medium'
  isReadOnly?: boolean
  isSubmitting?: boolean
}

export const FormRadioButton: FC<FormRadioButtonProps> = ({
  title,
  subtitle,
  isSelected,
  isCentered = true,
  isMobileFullWidth = true,
  onChange,
  isDisabled = false,
  size = 'medium',
  isReadOnly,
  isSubmitting,
  ...props
}) => {
  const isInactive = isSubmitting || isDisabled

  const textVariant = size === 'medium' ? TextVariant.text16 : TextVariant.text14

  return (
    <>
      <label
        className={cn(
          'label',
          isSelected && 'selected',
          isCentered && 'centered',
          isMobileFullWidth && 'full-width',
          isInactive && 'disabled',
          size,
        )}
        data-test={title}
      >
        <input
          type="radio"
          {...props}
          checked={isSelected}
          onChange={onChange}
          disabled={isInactive}
          readOnly={isReadOnly}
        />
        <Text
          variant={textVariant}
          fontWeight={TextFontWeight.semibold}
          color={isInactive && !isSelected ? theme.palette.gray[400] : theme.palette.text.default}
        >
          {title}
        </Text>
        {subtitle && (
          <Text
            variant={TextVariant.caption}
            element="p"
            color={isInactive && !isSelected ? theme.palette.gray[400] : theme.palette.text.light}
          >
            {subtitle}
          </Text>
        )}
      </label>

      <style jsx>{`
        .label {
          display: flex;
          flex-direction: column;
          justify-content: center;
          flex: 1;
          cursor: pointer;
          padding: ${theme.spacing[3]};
          border-radius: 12px;
          background-color: ${theme.palette.background.default};
          box-shadow: inset 0px 0px 0px 1px ${theme.palette.gray[200]};
          transition: background-color ${EXPAND_TRANSITION}ms ease-in-out,
            box-shadow ${EXPAND_TRANSITION}ms ease-in-out;
        }
        .small {
          flex: inherit;
          padding: ${theme.spacing[2]} ${theme.spacing[2.5]};
          border-radius: 8px;
        }
        .label + .label {
          margin-left: ${theme.spacing[2.5]};
        }
        .label:not(.disabled):hover {
          background-color: ${theme.palette.gray[50]};
        }
        .disabled {
          cursor: default;
        }
        .disabled:not(.selected) {
          background-color: ${theme.palette.gray[100]};
          box-shadow: none;
        }
        .selected {
          box-shadow: inset 0px 0px 0px 2px ${theme.palette.gray[900]};
        }
        .centered {
          align-items: center;
          text-align: center;
        }
        input {
          position: absolute;
          opacity: 0;
          height: 0;
          width: 0;
        }
        .label:not(.disabled):focus-within {
          border-radius: 12px;
          box-shadow: inset 0px 0px 0px 1px ${theme.palette.gray[300]},
            0px 0px 0px 4px ${theme.palette.gray[100]};
        }
        .label.small:not(.disabled):focus-within {
          border-radius: 8px;
        }
        .label.selected:not(.disabled):focus-within {
          box-shadow: inset 0px 0px 0px 2px ${theme.palette.gray[900]},
            0px 0px 0px 4px ${theme.palette.gray[100]};
        }
        @media ${MOBILE_SCREENSIZE} {
          .label + .label.full-width {
            margin-left: 0;
            margin-top: ${theme.spacing[3]};
          }
        }
      `}</style>
    </>
  )
}
